import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Link, useRouter } from '~/utils/navigation';
import { notificationClick, notificationMarkAllRead, notificationClickCategory } from '@comeon/analytics';

import NotificationDetail from './my-notifications-detail.js';
import { markRead, markAllRead } from './ducks/my-notifications.js';
import handleCTAClick, { allCategories } from './my-notifications-utils.js';
import RichTextRenderer from './rich-text-renderer';

import Icon, { ICON_BELL_LINE, ICON_RIGHT } from '~/components/icon/index.js';
import { HeadWrapper, MainTitle, SubTitle } from '~/components/misc/section-head.js';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer.js';
import useTranslations from '~/components/translation/use-translations.ts';
import ButtonWrapper from '~/components/misc/button-wrapper.js';

import Anchor from '~/components/misc/anchor.js';
import { Sidebar, SidebarHeader, SidebarContent } from '~/components/sidebar/index.js';

import IconArrowLeftButton from '~/components/misc/icon-arrow-left-button.js';
import selectEnabledCategories from '~/components/account/my-notifications/utils/enabled-categories-selector';
import usePreprocess from './utils/usePreprocess.js';
import useBasePathname from '~/hooks/use-base-pathname';

const textKeys = [
	'MY_NOTIFICATIONS_TITLE',
	'MY_NOTIFICATIONS_MARK_ALL_READ',
	'NO_NOTIFICATIONS_HEADER',
	'NO_NOTIFICATIONS_DESC',
	'NO_NOTIFICATIONS_CTA'
];

const sortNotificationsByDate = ar =>
	ar.sort((a, b) => {
		if (b.receivedNotification && a.receivedNotification) {
			return new Date(b.receivedNotification) - new Date(a.receivedNotification);
		}
		if (!b.receivedNotification && !a.receivedNotification) {
			return 0;
		}
		if (!b.receivedNotification) {
			return 1;
		}
		return -1;
	});

const dateTimeFormat = timeStamp => {
	const today = new Date().setHours(0, 0, 0, 0);
	const thatDay = new Date(timeStamp).setHours(0, 0, 0, 0);
	// Check if it's the same date as today
	if (today === thatDay) {
		// return time
		return new Date(timeStamp).toLocaleTimeString();
	}
	// return Date
	return new Date(timeStamp).toLocaleDateString();
};

const checkIfUnreadMessagesExist = (notifications, category) => {
	let unreadNotifications;
	if (category === `INBOX_CATEGORY_ALL`) {
		unreadNotifications = notifications.filter(notification => notification.readNotification === false);
	} else {
		unreadNotifications = notifications.filter(
			notification =>
				notification?.data?.NotificationCategory === category && notification.readNotification === false
		);
	}

	if (unreadNotifications.length > 0) {
		return true;
	}
	return false;
};

const NoItems = ({ translations }) => {
	const { NO_NOTIFICATIONS_HEADER, NO_NOTIFICATIONS_DESC, NO_NOTIFICATIONS_CTA } = translations;

	return (
		<>
			<div className="u-scroll-content-wrapper u-pb-xl">
				<HeadWrapper>
					<Icon type={ICON_BELL_LINE} />
					<MainTitle>{NO_NOTIFICATIONS_HEADER}</MainTitle>
					<SubTitle>{NO_NOTIFICATIONS_DESC}</SubTitle>
				</HeadWrapper>
			</div>
			<ButtonWrapper transparent>
				<Link href="/" className="btn btn-border-radius">
					{NO_NOTIFICATIONS_CTA}
				</Link>
			</ButtonWrapper>
		</>
	);
};

const LinkItem = ({ item }) => {
	const dispatch = useDispatch();
	const transformPath = useContextualPathTransformer();
	const router = useRouter();
	const _item = usePreprocess(item);

	const basePathname = useBasePathname();

	const itemTextKeys = ['NOTIFICATION_RECEIVED_PLACEHOLDER'];

	// Fetch the translations
	const { translations } = useTranslations(itemTextKeys);

	// Datalayer tracking
	// Individual notification Trigger on click on notification item in the list
	const trackClickToMyNotificationsItem = () => {
		const eveLabel = _item.hasDetails ? '' : ` - ${_item.ctaUrl}`;
		notificationClick(
			`${_item.recurringNotificationId ? `r${_item.recurringNotificationId} - ` : ''}${_item.id} - ${
				_item.readNotification ? 'READ' : 'UNREAD'
			} - ${_item.title} ${eveLabel}`
		);
	};

	const ItemContent = (
		<div className={`my-notification-item-wrapper ${_item.readNotification ? 'read' : ''}`}>
			<div className="my-notification-details">
				<div className="my-notification-title-bar">
					{/* Title */}
					<RichTextRenderer
						data={_item.title}
						key="item-title"
						additionalClassName="my-notification-title"
						additionalClassNameEditor="my-notification-title_editor"
					/>
					{(_item.hasDetails || _item.ctaUrl) && <Icon type={ICON_RIGHT} />}
				</div>
				{/* Subtitle */}
				<RichTextRenderer
					data={_item.subtitle}
					key="item-subtitle"
					additionalClassName="my-notification-subitle"
					additionalClassNameEditor="my-notification-subitle_editor"
				/>
				<div className="my-notification-time">
					{_item.receivedNotification
						? dateTimeFormat(new Date(_item.receivedNotification).getTime())
						: translations.NOTIFICATION_RECEIVED_PLACEHOLDER}
				</div>
			</div>
		</div>
	);

	// unclickable items don't need the anchor wrapper
	if (!_item.hasDetails && !_item.ctaUrl) {
		return <div key={_item.id}>{ItemContent}</div>;
	}

	return (
		<Anchor
			key={_item.id}
			onClick={() => {
				trackClickToMyNotificationsItem();
				if (_item && !_item.readNotification) {
					dispatch(markRead(_item.id));
				}
				if (!_item.hasDetails) {
					handleCTAClick(_item, router, basePathname);
				} else {
					router.push(transformPath(`/account/my-notifications/${_item.id}`));
				}
			}}
		>
			{ItemContent}
		</Anchor>
	);
};

const MyNotifications = () => {
	const ourRef = useRef(null);

	const dispatch = useDispatch();
	const router = useRouter();
	const transformPath = useContextualPathTransformer();
	const allNotifications = useSelector(state => state.myNotifications.allNotifications);
	const showInboxCategoryAll = useSelector(state => state.brand.myNotificationsInboxAllCategoryEnabled);
	const enabledCategories = useSelector(selectEnabledCategories);
	const { translations } = useTranslations([...textKeys, ...allCategories]);

	const unreadNotifications = [];
	const readNotfications = [];
	const [currentCategory, setCurrentCategory] = useState(showInboxCategoryAll ? allCategories[0] : allCategories[2]);
	const [isMouseDown, setIsMouseDown] = useState(false);
	const mouseCoords = useRef({
		startX: 0,
		startY: 0,
		scrollLeft: 0,
		scrollTop: 0
	});

	Object.values(allNotifications).forEach(item =>
		item.readNotification ? readNotfications.push(item) : unreadNotifications.push(item)
	);
	const notifications = [
		...sortNotificationsByDate(unreadNotifications),
		...sortNotificationsByDate(readNotfications)
	];
	// Filter the notifications as per the selected filters
	const categoryFilteredNotifications =
		currentCategory === allCategories[0]
			? notifications
			: notifications.filter(notification => notification?.data?.NotificationCategory === currentCategory);

	const { notificationId } = useParams();
	const notificationDetailItem = allNotifications[notificationId];

	const displayedCategories = [];
	enabledCategories.forEach((isEnabled, i) => {
		if (isEnabled) {
			displayedCategories.push(allCategories[i]);
		}
	});
	// const displayedCategories = showInboxCategoryAll ? allCategories : allCategories.slice(1, allCategories.length - 1);

	const handleDragStart = e => {
		if (!ourRef.current) return;
		const slider = ourRef.current.children[0];
		const startX = e.pageX - slider.offsetLeft;
		const { scrollLeft } = slider;
		mouseCoords.current = { startX, startY: 0, scrollLeft };
		setIsMouseDown(true);
		document.body.style.cursor = 'grabbing';
	};
	const handleDragEnd = () => {
		setIsMouseDown(false);
		if (!ourRef.current) return;
		document.body.style.cursor = 'default';
	};
	const handleDrag = e => {
		if (!isMouseDown || !ourRef.current) return;
		e.preventDefault();
		const slider = ourRef.current.children[0];
		const x = e.pageX - slider.offsetLeft;
		const walkX = x - mouseCoords.current.startX;
		slider.scrollLeft = mouseCoords.current.scrollLeft - walkX;
	};

	// Datalayer tracking
	// Individual notification Trigger on click on notification item in the list
	const trackClickMarkAllRead = () => {
		notificationMarkAllRead(unreadNotifications.length);
	};

	const changeCategory = newCategory => {
		setCurrentCategory(newCategory);
		notificationClickCategory(newCategory);
	};

	const filterAndMarkReadAutomatically = () => {
		// Only look at the first 10 notifications
		const notificationsList = notifications.slice(0, 10);

		// filter out the notifications meeting our usecase
		// Then select the first 4 from the list
		const notificationsToMarkRead = notificationsList
			.filter(item => {
				return !item.readNotification && !item.hasDetails && !item.ctaUrl && !item.ctaText;
			})
			.slice(0, 4);
		// Call mark read for all filtered notifications
		notificationsToMarkRead.forEach(individualNotification => dispatch(markRead(individualNotification.id)));
	};

	return notificationDetailItem && notificationDetailItem.hasDetails ? (
		<NotificationDetail item={notificationDetailItem} />
	) : (
		<div className="my-notifications-wrapper">
			<Sidebar>
				<SidebarHeader
					Icon={
						<IconArrowLeftButton
							onClick={() => {
								filterAndMarkReadAutomatically();
								router.push(transformPath('/account'));
							}}
							className="js-back page-head__back u-show"
							data-at="backbutton-my-notifications"
						/>
					}
				>
					<h2 className="page-head__title" data-at="my-notifications-title">
						{translations.MY_NOTIFICATIONS_TITLE}
					</h2>
					{notifications.length > 0 ? (
						<button
							className="page-head__right-side"
							onClick={() => {
								dispatch(markAllRead());
								trackClickMarkAllRead();
							}}
						>
							{translations.MY_NOTIFICATIONS_MARK_ALL_READ}
						</button>
					) : (
						<div className="page-head__right-side" />
					)}
				</SidebarHeader>
				<SidebarContent className="my-notifications-list-wrapper">
					{notifications.length > 0 ? (
						<>
							{displayedCategories.length > 0 && (
								// eslint-disable-next-line jsx-a11y/no-static-element-interactions
								<div
									ref={ourRef}
									onMouseDown={handleDragStart}
									onMouseUp={handleDragEnd}
									onMouseMove={handleDrag}
								>
									<div className="my-notifications-allCategoryWrapper">
										{displayedCategories.map(category => (
											<span
												key={category}
												className={`my-notifications-individualCategory ${
													currentCategory === category
														? 'my-notifications-current-category'
														: ''
												} ${
													checkIfUnreadMessagesExist(notifications, category)
														? 'my-notifications-has-unread'
														: ''
												}`}
												onClick={() => {
													changeCategory(category);
												}}
												aria-hidden="true"
											>
												{translations[category]}
											</span>
										))}
									</div>
								</div>
							)}
							{categoryFilteredNotifications.length > 0 ? (
								categoryFilteredNotifications.map(item => <LinkItem item={item} key={item.id} />)
							) : (
								<div className="u-scroll-content-wrapper u-pb-xl">
									<HeadWrapper>
										<Icon type={ICON_BELL_LINE} />
										<MainTitle>{translations.NO_NOTIFICATIONS_HEADER}</MainTitle>
										<SubTitle>{translations.NO_NOTIFICATIONS_DESC}</SubTitle>
									</HeadWrapper>
								</div>
							)}
						</>
					) : (
						<NoItems translations={translations} />
					)}
				</SidebarContent>
			</Sidebar>
		</div>
	);
};

export default MyNotifications;
